import { z } from "zod";

export const invoice = z.object({
  invoiceNumber: z.string(),
  contact: z.object({
    id: z.string(),
    businessId: z.string(),
    fullName: z.string(),
    alias: z.string(),
    phoneNumber: z.string(),
    email: z.string(),
    color: z.string(),
    address: z.string(),
    businessName: z.string(),
    businessLogo: z.string(),
  }),
  supplier: z.object({
    id: z.string(),
    businessId: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phoneNumber: z.string(),
    businessName: z.string(),
    businessLogo: z.string(),
  }),
  amountPaid: z.number(),
  businessId: z.string(),
  issuedDate: z.string(),
  dueDate: z.string(),
  status: z.enum(["DRAFT", "SENT", "VIEWED", "PARTIALLY_PAID", "PAID", "VOID"]),
  total: z.number(),
  markedAsPaid: z.boolean().optional(),
  bankAccounts: z.array(
    z.object({
      bankName: z.string().min(1, { message: "Bank name is required" }),
      accountName: z.string().min(1, { message: "Account name is required" }),
      accountNumber: z
        .string()
        .min(1, { message: "Account number is required" })
        .length(10, { message: "Account number must be 10 digits" })
        .regex(/^\d+$/, {
          message: "Account number must only contain numbers",
        }),
    }),
  ),
  internalNote: z.string(),
  items: z.array(
    z.object({
      quantity: z.number(),
      product: z.object({
        name: z.string(),
        id: z.string(),
      }),
      variant: z.object({
        name: z.string(),
        id: z.string(),
        price: z.number(),
        unit: z.string().optional(),
        sku: z.string().optional(),
      }),
      id: z.string(),
    }),
  ),
  payments: z.array(
    z.object({
      id: z.string(),
      payerBusinessId: z.string(),
      recipientBusinessId: z.string(),
      amount: z.number(),
      paymentDate: z.string().datetime(),
      method: z.string(),
      appliedAmount: z.number(),
      note: z.string().nullable(),
      reference: z.string(),
      createdById: z.string(),
      status: z.enum(["PARTIALL_APPLIED", "APPLIED"]),
      supportingDocumentStorageKey: z.string().nullable(),
      createdAt: z.string().datetime(),
      updatedAt: z.string().datetime(),
    }),
  ),
  __v: z.number(),
  outlet: z.object({
    name: z.string(),
    id: z.string(),
  }),
  id: z.string(),
});

export const invoices = z.array(invoice);

export const newInvoice = z.object({
  outletId: z.string().optional(),
  contactId: z.string().min(1, { message: "Contact is required" }),
  issuedDate: z.string().min(1, { message: "Issue date is required" }),
  dueDate: z.string().min(1, { message: "Due date is required" }),
  products: z.array(
    z.object({
      name: z.string().min(1, { message: "Product is required" }),
      productId: z.string().min(1, { message: "Product is required" }),
      quantity: z.number().positive({ message: "Quantity is required" }),
      variant: z.object({
        name: z.string().min(1, { message: "Variant is required" }),
        id: z.string().min(1, { message: "Variant is required" }),
        price: z.number().positive({ message: "Price is required" }),
        unit: z.string().optional(),
        sku: z.string().optional(),
      }),
    }),
  ),
  internalNote: z.string().optional(),
  externalNote: z.string().optional(),
  status: z.enum(["DRAFT", "SENT", "VIEWED", "PARTIALLY_PAID", "PAID", "VOID"]),
  bankAccounts: z.array(
    z.object({
      bankName: z.string().min(1, { message: "Bank is required" }),
      accountName: z.string().min(1, { message: "Account name is required" }),
      accountNumber: z
        .string()
        .min(1, { message: "Account number is required" })
        .length(10, { message: "Account number must be 10 digits" })
        .regex(/^\d+$/, {
          message: "Account number must only contain numbers",
        }),
    }),
  ),
});

export const draftInvoice = z.object({
  outletId: z.string().optional(),
  contactId: z.string().min(1, { message: "Contact is required" }),
  issuedDate: z.string().optional(),
  dueDate: z.string().optional(),
  products: z
    .array(
      z.object({
        name: z.string().optional(),
        productId: z.string().optional(),
        quantity: z.number().optional(),
        variant: z.object({
          name: z.string().optional(),
          id: z.string().optional(),
          price: z.number().optional(),
          unit: z.string().optional(),
          sku: z.string().optional(),
        }),
      }),
    )
    .optional(),
  internalNote: z.string().optional(),
  externalNote: z.string().optional(),
  status: z.string().includes("DRAFT"),
  bankAccounts: z
    .array(
      z.object({
        bankName: z.string().min(1, { message: "Bank is required" }),
        accountName: z.string().min(1, { message: "Account name is required" }),
        accountNumber: z
          .string()
          .min(1, { message: "Account number is required" })
          .length(10, { message: "Account number must be 10 digits" })
          .regex(/^\d+$/, {
            message: "Account number must only contain numbers",
          }),
      }),
    )
    .optional(),
});

export type Invoice = z.infer<typeof invoice>;
export type Invoices = z.infer<typeof invoices>;
export type NewInvoice = z.infer<typeof newInvoice>;
export type DraftInvoice = z.infer<typeof draftInvoice>;

export type InvoiceItem = {
  quantity: number;
  id: string;
  product: {
    id: string;
    name: string;
  };
  variant: {
    id: string;
    name: string;
    price: number;
    unit?: string;
    sku?: string;
  };
};

export type NewInvoiceItem = {
  name: string;
  quantity: number;
  productId: string;
  product: {
    id: string;
    name: string;
  };
  variant: {
    id: string;
    name: string;
    price: number;
    unit?: string;
    sku?: string;
  };
  [key: string]: string | number | object;
};

export type InvoiceBankAccount = {
  bankName: string;
  accountName: string;
  accountNumber: string;
};

export enum InvoiceStatus {
  DRAFT = "DRAFT",
  SENT = "SENT",
  VIEWED = "VIEWED",
  PARTIAL = "PARTIALLY_PAID",
  PAID = "PAID",
  VOID = "VOID",
}

export enum PaymentMethods {
  CENTIPAY = "centipay",
  PAYSTACK = "paystack",
  FLUTTERWAVE = "flutterwave",
  BANK_TRANSFER = "bank_transfer",
}
